import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/sharp-solid-svg-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import Button, { ButtonSize, ButtonVariant } from '@/components/button';
import NumberInput from '@/components/input/numberInput';
import Select from '@/components/select';
import Typography from '@/components/typography';
import { GLOBAL_TOOLTIP } from '@/constants/constants';
import { CompFilterFormData } from '@/features/comps/comp-filter';
import { SoldLandFilterFields } from '@/features/comps/comp-filter/filterSchema';
import {
  MortgageFiltersFormNames,
  MortgageFiltersObject,
} from '@/features/mortgage/mortgageFilters/mortgageFilters';
import type { AdvancedSearchFormFields } from '@/features/search/advanced/constants';
import type { LeadListFormData } from '@/features/search/leadLists/helpers/typesAndConstants';
import { LeadListSearchInputNames } from '@/features/search/leadLists/helpers/typesAndConstants';
import { IAdvancedSearchFormData } from '@/models/search';

export enum FilterSoilIndexes {
  NCCPI = 'NCCPI',
  CPI = 'CPI',
  CSR2 = 'CSR2',
  PI = 'PI',
  WAPI = 'WAPI',
}

export const soilIndexRange: Record<
  FilterSoilIndexes,
  { max: string; min: string }
> = {
  [FilterSoilIndexes.NCCPI]: { max: '100', min: '0' },
  [FilterSoilIndexes.CPI]: { max: '100', min: '0' },
  [FilterSoilIndexes.CSR2]: { max: '100', min: '0' },
  [FilterSoilIndexes.PI]: { max: '147', min: '47' },
  [FilterSoilIndexes.WAPI]: { max: '200', min: '0' },
};

export type QuerySoilTypes = 'nccpi' | 'ia' | 'il' | 'in' | 'mn' | 'sd' | 'nd';

/**
 * Record for converting the soil score type to the value used by the
 *   query consolidated comps endpoint
 * THIS DOES NOT INCLUDE MN, SD, and ND because all of those use a specific CPI score
 */
export const SoilScoreFilterRecord: Record<
  Exclude<FilterSoilIndexes, FilterSoilIndexes.CPI>,
  QuerySoilTypes
> = {
  [FilterSoilIndexes.NCCPI]: 'nccpi',
  [FilterSoilIndexes.CSR2]: 'ia',
  [FilterSoilIndexes.PI]: 'il',
  [FilterSoilIndexes.WAPI]: 'in',
};

export const getQuerySoilTypes = (
  index: FilterSoilIndexes,
  state?: string
): QuerySoilTypes => {
  if (index === FilterSoilIndexes.CPI) {
    switch (state) {
      case '27': // Minnesota
        return 'mn';
      case '46': // South Dakota
        return 'sd';
      case '38': // North Dakota
        return 'nd';
      default:
        return 'sd';
    }
  }
  return SoilScoreFilterRecord[index];
};

export type CompatibleFormData =
  | CompFilterFormData
  | MortgageFiltersObject
  | IAdvancedSearchFormData
  | LeadListFormData;

type CompatibleFormFields =
  | SoldLandFilterFields
  | MortgageFiltersFormNames
  | AdvancedSearchFormFields
  | LeadListSearchInputNames;

type Props = {
  form: UseFormReturn<CompatibleFormData, any, CompatibleFormData>;
  soilScoreIndex: CompatibleFormFields;
  soilScoreMin: CompatibleFormFields;
  soilScoreMax: CompatibleFormFields;
  disabled?: boolean;
};

const soilIndexOptions = Object.keys(FilterSoilIndexes).map((val) => {
  return {
    label: val,
    value: val,
  };
});

const SoilFilter: React.FC<Props> = ({
  form,
  soilScoreIndex,
  soilScoreMin,
  soilScoreMax,
  disabled = false,
}) => {
  const { control, setValue, watch, trigger } = form;

  const selectedIndex = watch(soilScoreIndex) as FilterSoilIndexes;
  const soilScoreMinValue = watch(soilScoreMin) as number;
  const soilScoreMaxValue = watch(soilScoreMax) as number;

  const [prevSelectedIndex, setPrevSelectedIndex] = useState(selectedIndex);

  const disableNumFields = useMemo(() => {
    return selectedIndex === null || selectedIndex === undefined || disabled;
  }, [disabled, selectedIndex]);

  const clearSoilFilter = useCallback(() => {
    setValue(soilScoreIndex, null);
    setValue(soilScoreMax, null);
    setValue(soilScoreMin, null);
  }, [setValue, soilScoreIndex, soilScoreMin, soilScoreMax]);

  const soilLabel = useMemo(() => {
    if (!selectedIndex) return 'Soil Score Range (0-100)';

    const selectedSoilIndexRange = soilIndexRange[selectedIndex];

    return `Soil Score Range (${selectedSoilIndexRange.min}-${selectedSoilIndexRange.max})`;
  }, [selectedIndex]);

  // Checking if ranges are valid when soilIndex changes
  useEffect(() => {
    if (prevSelectedIndex === selectedIndex) return;

    if (!selectedIndex) {
      clearSoilFilter();
      return;
    }

    trigger(soilScoreMin);
    trigger(soilScoreMax);

    setPrevSelectedIndex(selectedIndex);
  }, [
    prevSelectedIndex,
    selectedIndex,
    trigger,
    soilScoreMax,
    soilScoreMin,
    clearSoilFilter,
  ]);

  // Revalidate both inputs when either changes.
  useEffect(() => {
    trigger(soilScoreMin);
    trigger(soilScoreMax);
  }, [
    trigger,
    soilScoreMinValue,
    soilScoreMaxValue,
    soilScoreMin,
    soilScoreMax,
  ]);

  return (
    <div className="flex flex-col gap-y-6">
      {/* Title bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-2">
          <Typography variant="h5">Soil Score</Typography>
          <div
            className="flex items-center justify-center"
            data-tooltip-content="Selecting an area-specific soil index will limit results to the area which uses that index."
            data-tooltip-delay-show={300}
            data-tooltip-id={GLOBAL_TOOLTIP}
          >
            <FontAwesomeIcon
              className="text-green-50"
              icon={faCircleQuestion}
            />
          </div>
        </div>
        <Button
          disabled={disabled}
          size={ButtonSize.SMALL}
          variant={ButtonVariant.GHOST}
          onClick={clearSoilFilter}
        >
          Clear
        </Button>
      </div>
      {/* Main body section */}
      <div className="flex flex-col pt-2">
        <Select
          hidePlaceholder
          additionalProps={{ hideDefault: true }}
          control={control}
          disabled={disabled}
          label="Index"
          name={soilScoreIndex}
          options={soilIndexOptions}
          showEndAdornment={false}
          onChange={(x) => {
            if (!x) return;
            setValue(soilScoreIndex, x);
          }}
        />
        <div className={twMerge('mb-2 flex', disableNumFields && 'opacity-30')}>
          <Typography variant="button2">{soilLabel}</Typography>
          <Typography className="text-red-utility" variant="button2">
            *
          </Typography>
        </div>

        <div className="flex flex-row gap-x-2">
          <NumberInput
            required
            control={control}
            disabled={disableNumFields}
            name={soilScoreMin}
            showEndAdornment={false}
          />
          <div className="decorative-dash mt-[20px] flex h-[3px] min-w-[8px] bg-gray-70 leading-[40px]" />
          <NumberInput
            control={control}
            disabled={disableNumFields}
            name={soilScoreMax}
            showEndAdornment={false}
          />
        </div>
      </div>
    </div>
  );
};

export default SoilFilter;
